import { useEffect, useRef, useState } from "react";

import { barcodeDetectorSupported } from "./qr-scan";

// jsQR is dynamically imported only when the fallback decoder actually starts (iOS WebKit), so
// Android/Chromium users — and the initial bundle — never pay for it.
type JsQrFn = typeof import("jsqr").default;

// Live QR scanner with a DUAL decoder so iPhone works:
//   - BarcodeDetector (native, fast) where the browser has it — Chromium/Android.
//   - jsQR frame decoding (canvas + getImageData) everywhere else — iOS WebKit has NO
//     BarcodeDetector in any browser (Safari, Brave, Chrome-on-iOS are all WebKit), and iPhone is
//     the pilot device, so the camera requirement is getUserMedia ONLY.
// This is browser-integration glue verified on real devices (jsdom has none of these APIs); it
// FAILS SAFE — a missing/denied camera calls onError with a guard-facing message rather than
// throwing, and always stops its tracks on unmount.

interface DetectedBarcode {
  rawValue: string;
}
interface BarcodeDetectorLike {
  detect(source: CanvasImageSource): Promise<DetectedBarcode[]>;
}
type BarcodeDetectorCtor = new (options?: { formats?: string[] }) => BarcodeDetectorLike;

const DETECT_INTERVAL_MS = 350;
// Decode frames at a capped width — full 4K frames make jsQR needlessly slow on phones.
const JSQR_MAX_WIDTH = 640;

export function QrScanner({
  onToken,
  onClose,
  onError,
}: {
  onToken: (rawValue: string) => void;
  onClose: () => void;
  onError: (message: string) => void;
}) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  // Keep the latest callbacks without restarting the camera on every render.
  const onTokenRef = useRef(onToken);
  const onErrorRef = useRef(onError);
  onTokenRef.current = onToken;
  onErrorRef.current = onError;
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let stream: MediaStream | null = null;
    let timer: ReturnType<typeof setInterval> | null = null;
    let stopped = false;

    function stop(): void {
      stopped = true;
      if (timer !== null) {
        clearInterval(timer);
        timer = null;
      }
      if (stream) {
        stream.getTracks().forEach((track) => track.stop());
        stream = null;
      }
    }

    function emitToken(value: string): void {
      if (stopped) {
        return;
      }
      stop();
      onTokenRef.current(value);
    }

    /** Native path: the browser decodes frames for us. */
    function startDetectorLoop(video: HTMLVideoElement, Detector: BarcodeDetectorCtor): void {
      const detector = new Detector({ formats: ["qr_code"] });
      timer = setInterval(() => {
        if (stopped) {
          return;
        }
        void detector
          .detect(video)
          .then((barcodes) => {
            const hit = barcodes.find(
              (barcode) => typeof barcode.rawValue === "string" && barcode.rawValue.length > 0,
            );
            if (hit) {
              emitToken(hit.rawValue);
            }
          })
          .catch(() => {
            // Transient per-frame decode error — keep polling.
          });
      }, DETECT_INTERVAL_MS);
    }

    /** Fallback path (iOS WebKit et al.): draw frames to a canvas and decode with jsQR. */
    function startJsQrLoop(video: HTMLVideoElement, jsQR: JsQrFn): void {
      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d", { willReadFrequently: true });
      if (!context) {
        stop();
        onErrorRef.current("Couldn't start the camera decoder. Use the manual fallback instead.");
        return;
      }
      timer = setInterval(() => {
        if (stopped || video.readyState < 2 || video.videoWidth === 0) {
          return;
        }
        try {
          const scale = Math.min(1, JSQR_MAX_WIDTH / video.videoWidth);
          const width = Math.max(1, Math.round(video.videoWidth * scale));
          const height = Math.max(1, Math.round(video.videoHeight * scale));
          if (canvas.width !== width || canvas.height !== height) {
            canvas.width = width;
            canvas.height = height;
          }
          context.drawImage(video, 0, 0, width, height);
          const image = context.getImageData(0, 0, width, height);
          const code = jsQR(image.data, width, height, { inversionAttempts: "dontInvert" });
          if (code && code.data.length > 0) {
            emitToken(code.data);
          }
        } catch {
          // Transient frame failure (e.g. zero-size frame during rotation) — keep polling.
        }
      }, DETECT_INTERVAL_MS);
    }

    async function start(): Promise<void> {
      const mediaDevices = typeof navigator !== "undefined" ? navigator.mediaDevices : undefined;
      if (!mediaDevices || typeof mediaDevices.getUserMedia !== "function") {
        onErrorRef.current("Camera scanning isn't available on this device.");
        return;
      }
      try {
        const media = await mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
        stream = media;
        const video = videoRef.current;
        if (stopped || !video) {
          stop();
          return;
        }
        video.srcObject = media;
        await video.play();
        if (stopped) {
          stop();
          return;
        }
        setReady(true);
        const Detector = barcodeDetectorSupported(window)
          ? (window as unknown as { BarcodeDetector: BarcodeDetectorCtor }).BarcodeDetector
          : null;
        if (Detector) {
          startDetectorLoop(video, Detector);
        } else {
          const { default: jsQR } = await import("jsqr");
          if (stopped) {
            stop();
            return;
          }
          startJsQrLoop(video, jsQR);
        }
      } catch {
        stop();
        onErrorRef.current("Couldn't access the camera. Check permissions, or use the manual fallback.");
      }
    }

    void start();
    return stop;
  }, []);

  return (
    <div className="qr-scanner">
      <video
        ref={videoRef}
        className="qr-scanner__video"
        muted
        playsInline
        aria-label="Camera viewfinder"
      />
      <p className="qr-scanner__hint" role="status">
        {ready ? "Point the camera at the checkpoint QR code." : "Starting camera…"}
      </p>
      <button className="qr-scanner__cancel" type="button" onClick={onClose}>
        Cancel
      </button>
    </div>
  );
}
